import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Mail, Send, Clock, Users, CheckCircle,
  ChevronRight, Eye, Loader2, Calendar, FileText
} from "lucide-react";

const ProgressReportsPanel = ({ open, onOpenChange }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (open) fetchReports();
  }, [open]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/progress-reports`);
      if (res.ok) setReports(await res.json());
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  const sendToAll = async () => {
    setSending(true);
    try {
      const res = await authFetch(`${API}/consultant/progress-reports/send`, {
        method: "POST",
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Progress reports sent to ${data.sent} client(s)!`);
        if (data.errors?.length) {
          toast.warning(`${data.errors.length} error(s) occurred`);
        }
        fetchReports();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to send reports");
      }
    } catch (_e) {
      toast.error("Send failed");
    }
    setSending(false);
  };

  const sendToClient = async (clientId) => {
    setSending(true);
    try {
      const res = await authFetch(`${API}/consultant/progress-reports/send`, {
        method: "POST",
        body: JSON.stringify({ client_id: clientId })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Report sent to ${data.sent} client!`);
        fetchReports();
      } else {
        toast.error("Failed to send report");
      }
    } catch (_e) {
      toast.error("Send failed");
    }
    setSending(false);
  };

  const previewReport = async (clientId) => {
    try {
      const res = await authFetch(`${API}/consultant/progress-reports/preview/${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data.report);
        setPreviewHtml(data.html_preview);
      } else {
        toast.error("Failed to load preview");
      }
    } catch (_e) {
      toast.error("Preview failed");
    }
  };

  // Group reports by month
  const reportsByMonth = reports.reduce((acc, r) => {
    const date = new Date(r.sent_at);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <>
      <Dialog open={open && !previewHtml} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="progress-reports-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Mail className="w-5 h-5 text-emerald-600" /> Monthly Progress Reports
            </DialogTitle>
            <DialogDescription>
              Automated reports sent on the 1st of each month. You can also send manually.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-2">
            <p className="text-sm text-gray-500">{reports.length} reports sent total</p>
            <Button
              onClick={sendToAll}
              disabled={sending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-all-reports-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-1.5" /> Send to All Clients</>
              )}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Clock className="w-5 h-5 animate-spin text-gray-400 mr-2" /> Loading...
            </div>
          ) : reports.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="font-medium text-gray-900">No reports sent yet</h3>
                <p className="text-sm text-gray-500 mt-1">Click "Send to All Clients" to send your first batch of monthly progress reports.</p>
                <p className="text-xs text-gray-400 mt-3">Reports are also auto-sent on the 1st of each month.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(reportsByMonth).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthReports]) => {
                const date = new Date(month + "-01");
                const label = date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
                return (
                  <div key={month}>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <h4 className="text-sm font-semibold text-gray-600">{label}</h4>
                      <Badge variant="outline" className="text-xs">{monthReports.length} sent</Badge>
                    </div>
                    <div className="space-y-1.5">
                      {monthReports.map((r) => (
                        <div key={r.report_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{r.client_name}</p>
                              <p className="text-xs text-gray-500">{r.client_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${r.status === "sent_auto" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {r.status === "sent_auto" ? "Auto" : "Manual"}
                            </Badge>
                            <span className="text-xs text-gray-400">{new Date(r.sent_at).toLocaleDateString()}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => sendToClient(r.client_id)}
                              disabled={sending}
                              data-testid={`resend-${r.report_id}`}
                            >
                              <Send className="w-3.5 h-3.5 text-gray-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewHtml} onOpenChange={() => { setPreviewHtml(null); setPreviewData(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4" /> Report Preview — {previewData?.name}
            </DialogTitle>
          </DialogHeader>
          {previewHtml && (
            <div className="border rounded-lg overflow-hidden">
              <iframe
                srcDoc={previewHtml}
                title="Report Preview"
                className="w-full h-[500px] border-0"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewHtml(null); setPreviewData(null); }}>Close</Button>
            {previewData?.client_id && (
              <Button
                onClick={() => { sendToClient(previewData.client_id); setPreviewHtml(null); setPreviewData(null); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={sending}
              >
                <Send className="w-4 h-4 mr-1" /> Send This Report
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProgressReportsPanel;
